import * as React from "react";
import * as ReactDOM from "react-dom";
import {
  AssessmentResult,
  ICourseComponentPart,
  IGraphFilters,
  IUnitInstanceAnalytics,
  IUnitsAnalytics,
  IUser,
  IUsersAnalytics,
  OnSelectAssessmentFn,
  UnitChartType,
  UserFilter,
} from "../../../App/Analytics/Common";
import { PreferredLocale } from "../../../utils/locales";
import AssessmentsAnalytics from "./AssessmentAnalytics/AssessmentsAnalytics";
import CourseCompletionGraph from "./CourseCompletion/CourseCompletionGraph";
import ExercisesAnalytics from "./UnitsAnalytics/QuestionsAndExercises/ExercisesAnalytics";
import IndividualAssessmentAnalytics from "./UnitsAnalytics/QuestionsAndExercises/IndividualAssessmentAnalytics";
import LocaleFilterSelection, {
  ILocaleFilterSelectionBaseProps,
} from "./UnitsAnalytics/LocaleFilter/LocaleFilterSelection";
import QuestionsAnalytics from "./UnitsAnalytics/QuestionsAndExercises/QuestionsAnalytics";
import TimeSpentGraph from "./TimeTaken/TimeSpentAnalytics";
import TopicsAnalytics from "./Lessons/TopicsAnalytics";
import UnitsAnalyticsGraph from "./UnitsAnalytics/UnitsAnalytics";
import { SectionContainer } from "./common/Charts.styles";

interface IGraphsPageProps extends IGraphFilters, ILocaleFilterSelectionBaseProps {
  courseDisplayName: string;
  courseId: string;
  masterLocale: PreferredLocale;
  objectDisplayName: string;
  onRemoveFilter: (name: string, type: UserFilter) => () => void;
  onSelectAssessmentName: OnSelectAssessmentFn;
  selected: Set<string>;
  unitInstanceAnalytics: IUnitInstanceAnalytics;
  unitsAnalytics: IUnitsAnalytics[];
  useSelectedAsFilter: boolean;
  users: IUser[];
  usersAnalytics: IUsersAnalytics;
}

export default class GraphsPage extends React.PureComponent<IGraphsPageProps> {
  public componentWillReceiveProps(nextProps: IGraphsPageProps) {
    if (this.props.selectedUnitName === nextProps.selectedUnitName) {
      return;
    }
    const node = ReactDOM.findDOMNode(this) as Element;
    if (node !== null) {
      node.scrollIntoView({ behavior: "smooth" });
    }
  }

  public render() {
    const {
      assessmentChartLabels,
      courseDisplayName,
      courseId,
      objectDisplayName,
      users,
      usersAnalytics,
      unitsAnalytics,
      selected,
      useSelectedAsFilter,
      timeTakenChartFilter,
      assessmentsChartFilter,
      onSelectUnitName,
      selectedUnitName,
      topicsChartFilter,
      fetchExerciseAnalytics,
      supportedLocales,
      onSelectLocale,
      selectedLocale,
      masterLocale,
      onSelectAssessmentName,
      onRemoveFilter,
      selectedAssessmentIdentifier,
      selectedAssessmentName,
      selectedAssessmentType,
      unitInstanceAnalytics,
    } = this.props;

    const {
      all,
      allCompleted,
      allStarted,
      assessments,
      onlyCompletedUsers,
      selectedCompleted,
      selectedStarted,
      selectedUsers,
    } = this.getData();
    const tag = selectedUnitName === null ? "course" : "topic";
    const selectedUnitLocalizedName =
      selectedUnitName === null
        ? []
        : unitsAnalytics.filter(ua => ua.name === selectedUnitName).flatMap(ua => ua.localizedUnitName);
    const questions =
      selectedUnitName === null
        ? []
        : unitsAnalytics.filter(ua => ua.name === selectedUnitName).flatMap(ua => ua.questions);
    const exercises =
      selectedUnitName === null
        ? []
        : unitsAnalytics
            .filter(ua => ua.name === selectedUnitName)
            .flatMap(ua => ua.exercises)
            .flatMap(e => e.exercises);
    const searchLocale: [PreferredLocale, PreferredLocale] | undefined = selectedLocale
      ? [selectedLocale, masterLocale]
      : undefined;
    if (selectedAssessmentName !== null) {
      const selectedIndividualAnswers = (e: ICourseComponentPart) => e.index === selectedAssessmentIdentifier;
      return (
        <IndividualAssessmentAnalytics
          courseDisplayName={courseDisplayName}
          courseId={courseId}
          analytics={unitInstanceAnalytics}
          locales={supportedLocales}
          selectedUsers={selected}
          users={users}
          masterLocale={masterLocale}
          selectedLocale={selectedLocale}
          onSelectLocale={onSelectLocale}
          courseAnalytics={usersAnalytics}
          individualAnswers={
            selectedAssessmentType === UnitChartType.Exercises
              ? exercises.find(selectedIndividualAnswers)
              : questions.find(selectedIndividualAnswers)
          }
          objectDisplayName={objectDisplayName}
          selectedUnitLocalizedName={selectedUnitLocalizedName}
        />
      );
    }

    return (
      <>
        {selectedUnitName === null && (
          <CourseCompletionGraph
            useSelectedAsFilter={useSelectedAsFilter}
            allCount={all}
            allStartedCount={allStarted}
            allCompletedCount={allCompleted}
            selectedCount={selectedUsers}
            selectedStartedCount={selectedStarted}
            selectedCompletedCount={selectedCompleted}
          />
        )}
        <TimeSpentGraph
          users={onlyCompletedUsers}
          usersAnalytics={usersAnalytics}
          selected={selected}
          timeTakenChartFilter={timeTakenChartFilter}
          onRemoveFilter={onRemoveFilter}
          tag={tag}
        />
        <AssessmentsAnalytics
          selectedFilters={assessmentChartLabels}
          assessments={assessments}
          chartFilter={assessmentsChartFilter}
          courseDisplayName={this.props.courseDisplayName}
          objectDisplayName={this.props.objectDisplayName}
        />
        <TopicsAnalytics
          useSelectedAsFilter={useSelectedAsFilter}
          users={onlyCompletedUsers}
          usersAnalytics={usersAnalytics}
          selected={selected}
          filterFunction={topicsChartFilter}
          tag={tag}
        />
        {selectedUnitName === null && (
          <UnitsAnalyticsGraph
            users={onlyCompletedUsers}
            unitsAnalytics={unitsAnalytics}
            selected={selected}
            selectUnitName={onSelectUnitName}
          />
        )}
        {selectedUnitName !== null && (
          <SectionContainer>
            {(questions.length > 0 || exercises.length > 0) && (
              <LocaleFilterSelection
                supportedLocales={supportedLocales}
                onSelectLocale={onSelectLocale}
                selectedLocale={selectedLocale}
                localeFilterType={UnitChartType.Questions}
              />
            )}
            <QuestionsAnalytics
              fetchAnalytics={fetchExerciseAnalytics}
              questions={questions}
              users={users}
              selectedUsers={selected}
              usersAnalytics={usersAnalytics}
              searchLocale={searchLocale}
              onSelectAssessmentName={onSelectAssessmentName}
            />
            <ExercisesAnalytics
              fetchAnalytics={fetchExerciseAnalytics}
              exercises={exercises}
              users={users}
              selectedUsers={selected}
              usersAnalytics={usersAnalytics}
              searchLocale={searchLocale}
              onSelectAssessmentName={onSelectAssessmentName}
            />
          </SectionContainer>
        )}
      </>
    );
  }

  private getData = () => {
    const { users, usersAnalytics, selected, useSelectedAsFilter } = this.props;
    let all: number = 0;
    let allCompleted: number = 0;
    let allStarted: number = 0;
    let selectedUsers: number = 0;
    let selectedCompleted: number = 0;
    let selectedStarted: number = 0;
    const onlyCompletedUsers: IUser[] = [];
    const assessments: AssessmentResult[][] = [];
    for (const user of users) {
      all++;
      const isSelectedUser = selected.has(user.accountId);
      if (isSelectedUser) {
        selectedUsers++;
      }
      const userAnalytics = usersAnalytics[user.accountId];
      if (userAnalytics) {
        if (userAnalytics.completed) {
          allCompleted++;
          onlyCompletedUsers.push(user);
          if (isSelectedUser) {
            selectedCompleted++;
          }
          if (!useSelectedAsFilter || isSelectedUser) {
            const userAssessments = userAnalytics.assessments;
            const assessmentsLength = userAssessments.length;
            for (let i = 0; i < assessmentsLength; i++) {
              const assessment = userAssessments[i];
              if (assessment.length > 0) {
                assessments.push(assessment);
              }
            }
          }
        }
        if (userAnalytics.lastSeen !== null && !userAnalytics.completed) {
          allStarted++;
          if (isSelectedUser) {
            selectedStarted++;
          }
        }
      }
    }
    return {
      all,
      allCompleted,
      allStarted,
      assessments,
      onlyCompletedUsers,
      selectedCompleted,
      selectedStarted,
      selectedUsers,
    };
  };
}
